import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import config from '../config/config';
import Skeleton from './common/Skeleton';

const FRAMEWORKS = [
  { id: 'nist',     label: 'NIST SP 800-53',        flag: '🇺🇸' },
  { id: 'iso27001', label: 'ISO/IEC 27001:2022',     flag: '🌐' },
  { id: 'cra',      label: 'EU Cyber Resilience Act', flag: '🇪🇺' },
];

const SEV_COLOR = {
  CRITICAL: 'danger',
  HIGH:     'warning',
  MEDIUM:   'info',
  LOW:      'secondary',
};

const GRADE_COLOR = { A: '#16a34a', B: '#0891b2', C: '#ca8a04', D: '#ea580c', F: '#dc2626' };

function formatRatio(value, total) {
  if (!total) return '0/0 (0%)';
  const pct = Math.round((value / total) * 1000) / 10;
  return `${value}/${total} (${pct}%)`;
}

function pct(value, total) {
  if (!total) return 0;
  return Math.round((value / total) * 100);
}

// ── CRA-specific dashboard ────────────────────────────────────────────────────

function CRADashboard({ repoName, sbomId }) {
  const { t } = useTranslation();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => {
    if (!repoName || !sbomId) return;
    setLoading(true);
    setError('');
    setData(null);
    const sbomName = sbomId.endsWith('-sbom.json') ? sbomId : `${sbomId}-sbom.json`;
    axios.get(`${config.BASE_URL}/sbom/${repoName}/${sbomName}/cra`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [repoName, sbomId]);

  if (loading) return <div style={{ padding: '1rem 0' }}><Skeleton.Table rows={6} cols={4} /></div>;
  if (error)   return <div className="alert alert-danger">{error}</div>;
  if (!data)   return null;

  const grade     = data.grade || 'F';
  const gradeClr  = GRADE_COLOR[grade] || '#dc2626';
  const score     = data.overallScore ?? 0;
  const ready     = data.isCRAReady;
  const mandatory = data.mandatoryFieldsPass;
  const stats     = data.stats || {};
  const vulns     = data.vulns;
  const sbomName  = sbomId.endsWith('-sbom.json') ? sbomId : `${sbomId}-sbom.json`;
  const reportUrl = `${config.BASE_URL}/sbom/${repoName}/${sbomName}/cra/report`;

  const checksArt20 = (data.checks || []).filter(c => !['art13_vuln_free','art14_patch_timeline'].includes(c.id));
  const checksVuln  = (data.checks || []).filter(c =>  ['art13_vuln_free','art14_patch_timeline'].includes(c.id));

  const issueBySev = { critical: [], warning: [], info: [] };
  (data.issues || []).forEach(i => (issueBySev[i.severity] || (issueBySev.info)).push(i));

  return (
    <div>
      {/* ── Score header ── */}
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.25rem', alignItems: 'stretch' }}>
        {/* Grade circle */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      minWidth: 110, background: 'var(--bs-body-bg)', border: '1px solid var(--bs-border-color)',
                      borderRadius: 12, padding: '1rem' }}>
          <div style={{ fontSize: '3rem', fontWeight: 800, lineHeight: 1, color: gradeClr }}>{grade}</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--bs-secondary-color)', marginTop: 4 }}>
            {score}/100
          </div>
          <div style={{ marginTop: 8 }}>
            {ready
              ? <span className="ce-sev" style={{ background: '#16a34a22', color: '#16a34a', border: '1px solid #16a34a55' }}>✅ CRA Ready</span>
              : <span className="ce-sev" style={{ background: '#dc262622', color: '#dc2626', border: '1px solid #dc262655' }}>⚠ Not Ready</span>
            }
          </div>
        </div>

        {/* Mandatory fields */}
        <div style={{ flex: '1 1 240px', background: 'var(--bs-body-bg)', border: `1px solid ${mandatory ? '#16a34a55' : '#dc262655'}`,
                      borderRadius: 12, padding: '0.875rem 1rem' }}>
          <div style={{ fontWeight: 700, fontSize: '0.8125rem', marginBottom: '0.5rem' }}>
            {t('cra.mandatoryFields', 'Mandatory Fields (Art. 20)')}
          </div>
          <MandatoryRow ok={stats.timestampPresent}     label={t('cra.timestamp',    'SBOM timestamp present')} />
          <MandatoryRow ok={stats.manufacturerPresent}  label={t('cra.manufacturer', 'Manufacturer declared')} />
          <MandatoryRow ok={(data.dependencyEdges || 0) > 0} label={t('cra.deps', 'Dependency relationships')} />
          <MandatoryRow ok={(stats.packagesWithPurl || 0) > 0} label={t('cra.purl', 'At least one PURL present')} />
        </div>

        {/* Package coverage */}
        <div style={{ flex: '1 1 220px', background: 'var(--bs-body-bg)', border: '1px solid var(--bs-border-color)',
                      borderRadius: 12, padding: '0.875rem 1rem' }}>
          <div style={{ fontWeight: 700, fontSize: '0.8125rem', marginBottom: '0.5rem' }}>
            {t('cra.packageCoverage', 'Package Coverage')}
          </div>
          <CoverageBar label="PURL"    value={pct(stats.packagesWithPurl  || 0, data.totalPackages)} color="#6366f1" />
          <CoverageBar label="CPE"     value={pct(stats.packagesWithCpe   || 0, data.totalPackages)} color="#0891b2" />
          <CoverageBar label={t('cra.hashes','Hashes')}  value={pct(stats.packagesWithHashes || 0, data.totalPackages)} color="#16a34a" />
          <CoverageBar label={t('cra.supplier','Supplier')} value={pct(stats.packagesWithSupplier || 0, data.totalPackages)} color="#ca8a04" />
        </div>

        {/* Download / report */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', justifyContent: 'center',
                      minWidth: 170, background: 'var(--bs-body-bg)', border: '1px solid var(--bs-border-color)',
                      borderRadius: 12, padding: '0.875rem 1rem' }}>
          <div style={{ fontWeight: 700, fontSize: '0.8125rem', marginBottom: '0.25rem' }}>
            📄 {t('cra.auditReport', 'Audit Report')}
          </div>
          <a href={reportUrl} target="_blank" rel="noopener noreferrer"
            className="ce-btn ce-btn--primary" style={{ textAlign: 'center', textDecoration: 'none' }}>
            👁 {t('cra.viewHtml', 'View HTML')}
          </a>
          <button className="ce-btn ce-btn--secondary"
            onClick={() => { const a = document.createElement('a'); a.href = reportUrl; a.download = `cra-${repoName}.html`; a.click(); }}>
            ⬇ {t('cra.download', 'Download')}
          </button>
        </div>
      </div>

      {/* ── Art. 13 / 14 vuln status ── */}
      {vulns && vulns.vulnScanAvailable && (
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
          <VulnStatusCard
            title={t('cra.art13', 'Art. 13 — Known Vulnerabilities')}
            pass={vulns.art13Pass}
            detail={vulns.art13Pass
              ? t('cra.art13Pass', 'No critical/high vulnerabilities')
              : `${vulns.criticalVulns} critical · ${vulns.highVulns} high unpatched`}
            extra={`${vulns.totalVulns} total CVEs`}
          />
          <VulnStatusCard
            title={t('cra.art14', 'Art. 14 — Patch Timelines')}
            pass={vulns.art14Pass}
            detail={vulns.art14Pass
              ? t('cra.art14Pass', 'No overdue remediations')
              : `${(vulns.overdueRemediations || []).length} overdue items`}
            extra={`${vulns.openRemediations || 0} open remediations`}
          />
        </div>
      )}

      {/* ── Art. 20 checks table ── */}
      {checksArt20.length > 0 && (
        <div className="ce-card" style={{ marginBottom: '1.25rem' }}>
          <div className="ce-card-header">
            <h6 className="ce-card-title">📋 {t('cra.art20Checks', 'Article 20 — SBOM Requirements')}</h6>
            <span className="ce-tag">{checksArt20.filter(c => c.pass).length}/{checksArt20.length} {t('cra.passing', 'passing')}</span>
          </div>
          <div className="ce-card-body" style={{ padding: 0 }}>
            <div className="ce-table-wrap" style={{ marginBottom: 0 }}>
              <table className="ce-table">
                <thead>
                  <tr>
                    <th>{t('cra.cols.requirement', 'Requirement')}</th>
                    <th>{t('cra.cols.article', 'Article')}</th>
                    <th>{t('cra.cols.status', 'Status')}</th>
                    <th style={{ minWidth: 150 }}>{t('cra.cols.coverage', 'Coverage')}</th>
                    <th>{t('cra.cols.detail', 'Detail')}</th>
                  </tr>
                </thead>
                <tbody>
                  {checksArt20.map(ch => (
                    <tr key={ch.id}>
                      <td style={{ fontWeight: 600, fontSize: '0.8125rem' }}>{ch.title}</td>
                      <td><span className="ce-mono" style={{ fontSize: '0.75rem' }}>{ch.articleRef || '—'}</span></td>
                      <td>
                        {ch.pass
                          ? <span className="ce-sev" style={{ background: '#16a34a22', color: '#16a34a', border: '1px solid #16a34a55' }}>✅ PASS</span>
                          : <span className="ce-sev" style={{ background: '#dc262622', color: '#dc2626', border: '1px solid #dc262655' }}>⚠ GAP</span>
                        }
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <div style={{ flex: 1, height: 6, background: 'var(--bs-border-color)', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{ width: `${ch.score}%`, height: '100%', background: ch.pass ? '#16a34a' : ch.score > 50 ? '#ca8a04' : '#dc2626', borderRadius: 3, transition: 'width 0.4s' }} />
                          </div>
                          <span style={{ fontSize: '0.75rem', color: 'var(--bs-secondary-color)', minWidth: 32 }}>{ch.score}%</span>
                        </div>
                      </td>
                      <td style={{ fontSize: '0.75rem', color: 'var(--bs-secondary-color)', maxWidth: 240 }}>{ch.detail || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Vulnerability enforcement checks ── */}
      {checksVuln.length > 0 && (
        <div className="ce-card" style={{ marginBottom: '1.25rem' }}>
          <div className="ce-card-header">
            <h6 className="ce-card-title">🛡 {t('cra.vulnEnforcement', 'Vulnerability Enforcement (Art. 13 & 14)')}</h6>
          </div>
          <div className="ce-card-body" style={{ padding: 0 }}>
            <div className="ce-table-wrap" style={{ marginBottom: 0 }}>
              <table className="ce-table">
                <thead>
                  <tr>
                    <th>{t('cra.cols.requirement', 'Requirement')}</th>
                    <th>{t('cra.cols.article', 'Article')}</th>
                    <th>{t('cra.cols.status', 'Status')}</th>
                    <th style={{ minWidth: 150 }}>{t('cra.cols.score', 'Score')}</th>
                    <th>{t('cra.cols.detail', 'Detail')}</th>
                  </tr>
                </thead>
                <tbody>
                  {checksVuln.map(ch => (
                    <tr key={ch.id}>
                      <td style={{ fontWeight: 600, fontSize: '0.8125rem' }}>{ch.title}</td>
                      <td><span className="ce-mono" style={{ fontSize: '0.75rem' }}>{ch.articleRef || '—'}</span></td>
                      <td>
                        {ch.pass
                          ? <span className="ce-sev" style={{ background: '#16a34a22', color: '#16a34a', border: '1px solid #16a34a55' }}>✅ PASS</span>
                          : <span className="ce-sev" style={{ background: '#dc262622', color: '#dc2626', border: '1px solid #dc262655' }}>❌ FAIL</span>
                        }
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <div style={{ flex: 1, height: 6, background: 'var(--bs-border-color)', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{ width: `${ch.score}%`, height: '100%', background: ch.pass ? '#16a34a' : '#dc2626', borderRadius: 3 }} />
                          </div>
                          <span style={{ fontSize: '0.75rem', color: 'var(--bs-secondary-color)', minWidth: 32 }}>{ch.score}%</span>
                        </div>
                      </td>
                      <td style={{ fontSize: '0.75rem', color: 'var(--bs-secondary-color)', maxWidth: 240 }}>{ch.detail || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Findings / issues ── */}
      {(data.issues || []).length > 0 && (
        <div className="ce-card">
          <div className="ce-card-header">
            <h6 className="ce-card-title">📌 {t('cra.findings', 'Compliance Findings')}</h6>
            <span className="ce-tag">{data.issues.length} {t('cra.total', 'total')}</span>
          </div>
          <div className="ce-card-body" style={{ padding: 0 }}>
            <div className="ce-table-wrap" style={{ marginBottom: 0 }}>
              <table className="ce-table">
                <thead>
                  <tr>
                    <th>{t('cra.cols.severity', 'Severity')}</th>
                    <th>{t('cra.cols.code', 'Code')}</th>
                    <th>{t('cra.cols.finding', 'Finding')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.issues.map((issue, i) => {
                    const sevCls = { critical: 'critical', warning: 'high', info: 'low' }[issue.severity] || 'none';
                    return (
                      <tr key={i}>
                        <td><span className={`ce-sev ce-sev--${sevCls}`}>{issue.severity?.toUpperCase()}</span></td>
                        <td><span className="ce-mono" style={{ fontSize: '0.75rem' }}>{issue.code}</span></td>
                        <td style={{ fontSize: '0.8125rem' }}>{issue.message}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MandatoryRow({ ok, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem', marginBottom: '0.3rem' }}>
      <span>{ok ? '✅' : '❌'}</span>
      <span style={{ color: ok ? 'var(--bs-body-color)' : '#dc2626' }}>{label}</span>
    </div>
  );
}

function CoverageBar({ label, value, color }) {
  return (
    <div style={{ marginBottom: '0.4rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: '0.15rem' }}>
        <span>{label}</span>
        <span style={{ color: 'var(--bs-secondary-color)' }}>{value}%</span>
      </div>
      <div style={{ height: 5, background: 'var(--bs-border-color)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${value}%`, height: '100%', background: color, borderRadius: 3, transition: 'width 0.4s' }} />
      </div>
    </div>
  );
}

function VulnStatusCard({ title, pass, detail, extra }) {
  return (
    <div style={{ flex: '1 1 220px', padding: '0.875rem 1rem',
                  background: 'var(--bs-body-bg)',
                  border: `1px solid ${pass ? '#16a34a55' : '#dc262655'}`,
                  borderRadius: 10 }}>
      <div style={{ fontWeight: 700, fontSize: '0.8125rem', marginBottom: '0.4rem' }}>{title}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
        {pass
          ? <span className="ce-sev" style={{ background: '#16a34a22', color: '#16a34a', border: '1px solid #16a34a55' }}>✅ PASS</span>
          : <span className="ce-sev" style={{ background: '#dc262622', color: '#dc2626', border: '1px solid #dc262655' }}>❌ FAIL</span>
        }
        <span style={{ fontSize: '0.8125rem' }}>{detail}</span>
      </div>
      <div style={{ fontSize: '0.75rem', color: 'var(--bs-secondary-color)' }}>{extra}</div>
    </div>
  );
}

// ── Generic NIST / ISO view (unchanged) ──────────────────────────────────────

export default function ComplianceView({ repoName, sbomId }) {
  const { t } = useTranslation();
  const [framework, setFramework] = useState('nist');
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [expandedCtrl, setExpandedCtrl] = useState(null);
  const [sevFilter, setSevFilter] = useState('ALL');

  useEffect(() => {
    if (!repoName || !sbomId || framework === 'cra') return;
    setLoading(true);
    setError('');
    setData(null);
    const sbomName = sbomId.endsWith('-sbom.json') ? sbomId : `${sbomId}-sbom.json`;
    axios.get(`${config.BASE_URL}/sbom/${repoName}/${sbomName}/compliance?framework=${framework}`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [repoName, sbomId, framework]);

  const fw = FRAMEWORKS.find(f => f.id === framework);
  const vulns = data?.vulns || [];
  const filtered = sevFilter === 'ALL' ? vulns : vulns.filter(v => v.severity === sevFilter);

  return (
    <div>
      {/* Framework selector */}
      <div className="ce-filter-bar">
        <span style={{ fontSize: '0.75rem', color: 'var(--bs-secondary-color)' }}>{t('compliance.frameworkLabel', 'Framework:')}</span>
        {FRAMEWORKS.map(f => (
          <button
            key={f.id}
            className={`ce-btn ce-btn--sm ${framework === f.id ? 'ce-btn--primary' : 'ce-btn--secondary'}`}
            onClick={() => setFramework(f.id)}
          >
            {f.flag} {f.label}
          </button>
        ))}
      </div>

      {/* CRA gets its own dashboard */}
      {framework === 'cra' && <CRADashboard repoName={repoName} sbomId={sbomId} />}

      {/* NIST / ISO views */}
      {framework !== 'cra' && (
        <>
          {loading && <div style={{ padding: '1rem 0' }}><Skeleton.Table rows={4} cols={4} /></div>}
          {error   && <div className="alert alert-danger">{error}</div>}
          {data && (
            <>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                <StatCard label={t('compliance.framework', 'Framework')}  value={fw?.label} sub={fw?.flag} color="primary" />
                <StatCard label={t('compliance.totalCves', 'Total CVEs')} value={data.totalVulns} color="dark" />
                <StatCard label={t('compliance.critical', 'Critical')}   value={data.critical}   color="danger" />
                <StatCard label={t('compliance.high', 'High')}           value={data.high}       color="warning" />
                <StatCard label={t('compliance.medium', 'Medium')}       value={data.medium}     color="info" />
                <StatCard label={t('compliance.low', 'Low')}             value={data.low}        color="secondary" />
              </div>

              {data.identifierReadiness?.totalPackages > 0 && (
                <div className="ce-card" style={{ marginBottom: '1rem' }}>
                  <div className="ce-card-header">
                    <h6 className="ce-card-title">{t('compliance.identifierTitle', 'Identifier readiness')}</h6>
                    <span className="ce-tag">
                      {formatRatio(
                        (data.identifierReadiness.packagesWithPurl || 0) + (data.identifierReadiness.packagesWithCpe || 0),
                        (data.identifierReadiness.totalPackages || 0) * 2
                      )} {t('compliance.identifierCoverage', 'coverage')}
                    </span>
                  </div>
                  <div className="ce-card-body">
                    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                      <StatCard label={t('compliance.purlCoverage', 'Usable PURLs')} value={formatRatio(data.identifierReadiness.packagesWithPurl || 0, data.identifierReadiness.totalPackages)} color="primary" />
                      <StatCard label={t('compliance.cpeCoverage',  'Usable CPEs')}  value={formatRatio(data.identifierReadiness.packagesWithCpe  || 0, data.identifierReadiness.totalPackages)} color="info" />
                      <StatCard label={t('compliance.enrichedIdentifiers', 'Enriched IDs')} value={(data.identifierReadiness.enrichedPurl || 0) + (data.identifierReadiness.enrichedCpe || 0)} color="success" />
                      <StatCard label={t('compliance.missingIdentifiers',  'Missing both')} value={data.identifierReadiness.packagesMissingBothIds || 0} color="warning" />
                    </div>
                  </div>
                </div>
              )}

              <div className="ce-section-header">
                <h6 className="ce-section-title">📋 {t('compliance.affectedControls', 'Affected Controls')}</h6>
              </div>
              {data.summary && data.summary.length > 0 ? (
                <div className="ce-grid-2">
                  {[...data.summary].sort((a, b) => b.vulnCount - a.vulnCount).map(cs => (
                    <div
                      key={cs.control.id}
                      className="ce-card"
                      style={{ cursor: 'pointer', borderLeft: expandedCtrl === cs.control.id ? '3px solid var(--accent)' : '3px solid var(--bs-border-color)' }}
                      onClick={() => setExpandedCtrl(expandedCtrl === cs.control.id ? null : cs.control.id)}
                    >
                      <div className="ce-card-body" style={{ padding: '0.75rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div>
                            <span className="ce-sev ce-sev--info" style={{ marginRight: '0.5rem' }}>{cs.control.id}</span>
                            <strong style={{ fontSize: '0.8125rem' }}>{cs.control.title}</strong>
                          </div>
                          <span className="ce-sev ce-sev--critical">{cs.vulnCount} CVEs</span>
                        </div>
                        <div style={{ marginTop: '0.375rem', display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                          {cs.severities.map(s => {
                            const cls = { CRITICAL: 'critical', HIGH: 'high', MEDIUM: 'medium', LOW: 'low' }[s] || 'none';
                            return <span key={s} className={`ce-sev ce-sev--${cls}`}>{s}</span>;
                          })}
                        </div>
                        {expandedCtrl === cs.control.id && (
                          <p style={{ fontSize: '0.8125rem', color: 'var(--bs-secondary-color)', marginTop: '0.5rem', marginBottom: 0 }}>
                            {cs.control.description}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="alert alert-success">{t('compliance.noVulnsSuccess', '✅ No vulnerabilities found — all controls satisfied.')}</div>
              )}

              {filtered.length > 0 && (
                <>
                  <div className="ce-section-header">
                    <h6 className="ce-section-title">🔍 {t('compliance.cveControlMapping', 'CVE → Control Mapping')}</h6>
                    <div className="ce-section-actions">
                      {['ALL','CRITICAL','HIGH','MEDIUM','LOW'].map(s => (
                        <button key={s}
                          className={`ce-btn ce-btn--sm ${sevFilter === s ? 'ce-btn--primary' : 'ce-btn--secondary'}`}
                          onClick={() => setSevFilter(s)}>{s}</button>
                      ))}
                    </div>
                  </div>
                  <div className="ce-table-wrap">
                    <table className="ce-table">
                      <thead>
                        <tr>
                          <th>{t('compliance.columns.cve')}</th>
                          <th>{t('compliance.columns.package')}</th>
                          <th>{t('compliance.columns.severity')}</th>
                          <th>{t('compliance.columns.cvss')}</th>
                          <th>{t('compliance.columns.mappedControls')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((v, i) => {
                          const sevCls = { CRITICAL: 'critical', HIGH: 'high', MEDIUM: 'medium', LOW: 'low' }[v.severity] || 'none';
                          return (
                            <tr key={i}>
                              <td>
                                <a href={`https://osv.dev/vulnerability/${v.vulnId}`} target="_blank" rel="noopener noreferrer"
                                  className="ce-mono" style={{ fontWeight: 700 }}>{v.vulnId}</a>
                                <div className="text-truncate" style={{ maxWidth: 220, fontSize: '0.75rem', color: 'var(--bs-secondary-color)' }} title={v.summary}>{v.summary}</div>
                              </td>
                              <td>
                                <span style={{ fontWeight: 600, fontSize: '0.8125rem' }}>{v.packageName}</span>
                                <span style={{ color: 'var(--bs-secondary-color)', marginLeft: '0.25rem', fontSize: '0.75rem' }}>{v.version}</span>
                              </td>
                              <td><span className={`ce-sev ce-sev--${sevCls}`}>{v.severity}</span></td>
                              <td className="ce-mono">{v.cvssScore > 0 ? v.cvssScore.toFixed(1) : '—'}</td>
                              <td>
                                {v.controls && v.controls.map(c => (
                                  <span key={c.id} className="ce-sev ce-sev--info" style={{ marginRight: '0.25rem', marginBottom: '0.25rem' }} title={c.description}>{c.id}</span>
                                ))}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, sub, color }) {
  const kpiColor = { primary: '#6366f1', dark: '#374151', danger: '#dc2626', warning: '#ca8a04', info: '#0891b2', secondary: '#6b7280', success: '#16a34a' }[color] || '#6366f1';
  return (
    <div className="ce-kpi" style={{ '--kpi-color': kpiColor, flex: '1 1 120px' }}>
      <div className="ce-kpi-value" style={{ fontSize: '1.25rem' }}>{sub && <span style={{ marginRight: '0.25rem' }}>{sub}</span>}{value ?? '—'}</div>
      <div className="ce-kpi-label">{label}</div>
    </div>
  );
}
